import { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useAIStatusStore } from '../stores/aiStatusStore';
import { api, type ApiKeyStatus, type ApiKeyConfig, type AITestResult } from '../services/api';

type Provider = 'anthropic' | 'openai' | 'gemini' | 'openrouter' | 'custom';

export function SettingsPage() {
  const { isAuthenticated } = useAuthStore();
  const { forceRefresh: refreshAIStatus } = useAIStatusStore();
  const [status, setStatus] = useState<ApiKeyStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<AITestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [apiKey, setApiKey] = useState('');
  const [provider, setProvider] = useState<Provider>('anthropic');
  const [baseUrl, setBaseUrl] = useState('');
  const [model, setModel] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Available models state
  const [availableModels, setAvailableModels] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadStatus();
    }
  }, [isAuthenticated]);

  // Reset model and available models when provider changes
  useEffect(() => {
    setModel('');
    setAvailableModels([]);
    setModelsError(null);
  }, [provider]);

  // Fetch available models when API key changes (with debounce)
  useEffect(() => {
    if (apiKey && apiKey.length > 10 && provider !== 'custom' && provider !== 'openrouter') {
      const timer = setTimeout(() => {
        fetchAvailableModels();
      }, 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [apiKey, provider]);

  const fetchAvailableModels = async () => {
    if (!apiKey || provider === 'custom' || provider === 'openrouter') return;

    setIsLoadingModels(true);
    setModelsError(null);

    try {
      const result = await api.fetchAIModels(provider, apiKey);
      if (result.success && result.models) {
        setAvailableModels(result.models);
        // Auto-select first model if none selected
        if (!model && result.models.length > 0) {
          setModel(result.models[0].id);
        }
      } else {
        setModelsError(result.message || 'Failed to fetch models');
      }
    } catch (err) {
      setModelsError(err instanceof Error ? err.message : 'Failed to fetch models');
    } finally {
      setIsLoadingModels(false);
    }
  };

  const loadStatus = async () => {
    try {
      const data = await api.getApiKeyStatus();
      setStatus(data);
    } catch (err) {
      console.error('Failed to load API key status:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSaving(true);

    try {
      const config: ApiKeyConfig = {
        apiKey,
        provider,
        baseUrl:
          provider === 'openrouter'
            ? (baseUrl || 'https://openrouter.ai/api/v1')
            : provider === 'custom'
              ? baseUrl
              : null,
        model: model || null,
      };

      const result = await api.saveApiKey(config);
      setSuccess(result.message);
      setShowForm(false);
      setApiKey('');
      setBaseUrl('');
      setModel('');
      await loadStatus();
      // Refresh AI status badge to show updated availability
      await refreshAIStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save API key');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to remove your API key?')) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const result = await api.deleteApiKey();
      setSuccess(result.message);
      setTestResult(null);
      await loadStatus();
      // Refresh AI status badge to show updated availability
      await refreshAIStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete API key');
    }
  };

  const handleTestConnection = async () => {
    setError(null);
    setSuccess(null);
    setTestResult(null);
    setIsTesting(true);

    try {
      const result = await api.testAIConnection();
      setTestResult(result);
    } catch (err) {
      // The error message from the API is more descriptive
      const errorMessage = err instanceof Error ? err.message : 'Failed to test AI connection';
      setTestResult({
        success: false,
        message: errorMessage,
        error: 'REQUEST_FAILED',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const getProviderLabel = (p: Provider): string => {
    switch (p) {
      case 'anthropic':
        return 'Anthropic (Claude)';
      case 'openai':
        return 'OpenAI';
      case 'gemini':
        return 'Google (Gemini)';
      case 'openrouter':
        return 'OpenRouter (200+ models)';
      case 'custom':
        return 'Custom (OpenAI-compatible)';
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-center text-muted-foreground">
          Please sign in to access settings.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <h1 className="text-3xl font-bold mb-8">Settings</h1>

      {/* AI API Key Configuration */}
      <section className="border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">AI API Key</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Configure your own AI API key to use advanced diagram generation features.
          Your API key is encrypted and stored securely.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-md text-sm text-green-600">
            {success}
          </div>
        )}

        {isLoading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : (
          <>
            {/* Current Status */}
            <div className="mb-6 p-4 bg-muted/50 rounded-md">
              <h3 className="text-sm font-medium mb-2">Current Status</h3>
              {status?.hasApiKey ? (
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="text-muted-foreground">Provider:</span>{' '}
                    <span className="font-medium">{getProviderLabel(status.provider)}</span>
                  </p>
                  {status.model && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">Model:</span>{' '}
                      <span className="font-medium">{status.model}</span>
                    </p>
                  )}
                  {status.baseUrl && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">Base URL:</span>{' '}
                      <span className="font-medium">{status.baseUrl}</span>
                    </p>
                  )}
                  <p className="text-sm text-green-600 mt-2">
                    Your API key is configured and active.
                  </p>

                  {/* Test Connection Button */}
                  <div className="mt-3 pt-3 border-t border-border">
                    <button
                      onClick={handleTestConnection}
                      disabled={isTesting}
                      className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {isTesting ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Testing...
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Test Connection
                        </>
                      )}
                    </button>

                    {/* Test Result */}
                    {testResult && (
                      <div className={`mt-3 p-3 rounded-md text-sm ${
                        testResult.success
                          ? 'bg-green-500/10 border border-green-500/20'
                          : 'bg-destructive/10 border border-destructive/20'
                      }`}>
                        <p className={testResult.success ? 'text-green-600' : 'text-destructive'}>
                          {testResult.message}
                        </p>
                        {testResult.success && testResult.latencyMs && (
                          <div className="mt-2 text-xs text-muted-foreground space-y-1">
                            <p>Model: {testResult.model}</p>
                            <p>Latency: {testResult.latencyMs}ms</p>
                            {testResult.usage && (
                              <p>Tokens used: {testResult.usage.inputTokens} in / {testResult.usage.outputTokens} out</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-muted-foreground">
                    No personal API key configured.
                  </p>
                  {status?.systemAiEnabled && (
                    <p className="text-sm text-muted-foreground mt-1">
                      System AI is available for basic features.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            {!showForm ? (
              <div className="flex gap-3">
                <button
                  onClick={() => setShowForm(true)}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  {status?.hasApiKey ? 'Update API Key' : 'Add API Key'}
                </button>
                {status?.hasApiKey && (
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 border border-destructive text-destructive rounded-md text-sm font-medium hover:bg-destructive/10 transition-colors"
                  >
                    Remove API Key
                  </button>
                )}
              </div>
            ) : (
              /* Configuration Form */
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label htmlFor="provider" className="block text-sm font-medium mb-1">
                    Provider
                  </label>
                  <select
                    id="provider"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value as Provider)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  >
                    <option value="anthropic">Anthropic (Claude)</option>
                    <option value="openai">OpenAI</option>
                    <option value="gemini">Google (Gemini)</option>
                    <option value="openrouter">OpenRouter (200+ models)</option>
                    <option value="custom">Custom (OpenAI-compatible)</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {provider === 'custom' && 'Use with Ollama, Mistral, vLLM, LiteLLM, or other OpenAI-compatible APIs.'}
                    {provider === 'anthropic' && 'Get your API key from console.anthropic.com'}
                    {provider === 'openai' && 'Get your API key from platform.openai.com'}
                    {provider === 'gemini' && 'Get your API key from aistudio.google.com'}
                    {provider === 'openrouter' && 'Get your API key from openrouter.ai/keys — access Claude, GPT-4o, Gemini, Llama, and 200+ models through a single key.'}
                  </p>
                </div>

                <div>
                  <label htmlFor="apiKey" className="block text-sm font-medium mb-1">
                    API Key
                  </label>
                  <input
                    id="apiKey"
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    placeholder={
                      provider === 'anthropic'
                        ? 'sk-ant-...'
                        : provider === 'gemini'
                          ? 'AIza...'
                          : provider === 'openrouter'
                            ? 'sk-or-...'
                            : 'sk-...'
                    }
                    required
                  />
                </div>

                {(provider === 'custom' || provider === 'openrouter') && (
                  <div>
                    <label htmlFor="baseUrl" className="block text-sm font-medium mb-1">
                      Base URL
                    </label>
                    <input
                      id="baseUrl"
                      type="url"
                      value={baseUrl || (provider === 'openrouter' ? 'https://openrouter.ai/api/v1' : '')}
                      onChange={(e) => setBaseUrl(e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-md bg-background"
                      placeholder={
                        provider === 'openrouter'
                          ? 'https://openrouter.ai/api/v1'
                          : 'http://localhost:11434/v1'
                      }
                      required={provider === 'custom'}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {provider === 'openrouter'
                        ? 'Pre-filled for OpenRouter. Change only if you use a proxy.'
                        : 'The base URL of your OpenAI-compatible API endpoint.'}
                    </p>
                  </div>
                )}

                <div>
                  <label htmlFor="model" className="block text-sm font-medium mb-1">
                    Model
                  </label>
                  {(provider === 'custom' || provider === 'openrouter') ? (
                    <input
                      id="model"
                      type="text"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-md bg-background"
                      placeholder={
                        provider === 'openrouter'
                          ? 'anthropic/claude-sonnet-4, openai/gpt-4o, google/gemini-2.5-flash, etc.'
                          : 'llama3.2, mistral-7b, etc.'
                      }
                    />
                  ) : (
                    <div className="relative">
                      <select
                        id="model"
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                        disabled={isLoadingModels || availableModels.length === 0}
                        className="w-full px-3 py-2 border border-input rounded-md bg-background disabled:opacity-50"
                      >
                        {availableModels.length === 0 ? (
                          <option value="">
                            {isLoadingModels ? 'Loading models...' : apiKey ? 'Validating API key...' : 'Enter API key to see available models'}
                          </option>
                        ) : (
                          availableModels.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))
                        )}
                      </select>
                      {isLoadingModels && (
                        <div className="absolute right-8 top-1/2 -translate-y-1/2">
                          <svg className="animate-spin h-4 w-4 text-muted-foreground" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  )}
                  {modelsError && (
                    <p className="text-xs text-destructive mt-1">
                      {modelsError}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {provider === 'custom'
                      ? 'Enter the model name for your API endpoint.'
                      : availableModels.length > 0
                        ? 'Select from models available with your API key.'
                        : 'Enter a valid API key to load available models.'}
                  </p>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save API Key'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setApiKey('');
                      setBaseUrl('');
                      setModel('');
                      setError(null);
                    }}
                    className="px-4 py-2 border border-border rounded-md text-sm font-medium hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </section>

      {/* Info Section */}
      <section className="mt-6 p-4 bg-muted/30 rounded-lg">
        <h3 className="text-sm font-medium mb-2">Supported Providers</h3>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>
            <strong>Anthropic (Claude)</strong> - Recommended for best diagram generation quality
          </li>
          <li>
            <strong>OpenAI</strong> - GPT-4 and GPT-4o models
          </li>
          <li>
            <strong>Google (Gemini)</strong> - Gemini 1.5 Pro and other Gemini models
          </li>
          <li>
            <strong>Custom</strong> - Any OpenAI-compatible API (Ollama, Mistral, vLLM, LiteLLM, etc.)
          </li>
        </ul>
      </section>
    </div>
  );
}
